// /var/www/oszczedzam/client/src/components/goals/SavingsAllocation.js
import React, { useState, useEffect } from 'react';
import { 
  Box, Typography, TextField, Button, Slider, Grid,
  Paper, InputAdornment, FormControl, InputLabel, Select,
  MenuItem, Divider, Alert, CircularProgress
} from '@mui/material';
import { useTransactions } from '../../hooks/useTransactions';

function SavingsAllocation({ goal, onAllocate, loading }) {
  const [amount, setAmount] = useState('');
  const [transactionDescription, setTransactionDescription] = useState(`Oszczędności na cel: ${goal?.name || ''}`);
  const [source, setSource] = useState('newTransaction');
  const [selectedTransaction, setSelectedTransaction] = useState('');
  const [error, setError] = useState('');
  
  const { transactions } = useTransactions();
  const [availableTransactions, setAvailableTransactions] = useState([]);
  
  // Formatuj kwotę
  const formatAmount = (amount) => {
    return amount.toLocaleString('pl-PL', { style: 'currency', currency: 'PLN' });
  };
  
  // Po załadowaniu transakcji, przefiltruj tylko przychody
  useEffect(() => {
    if (transactions && transactions.length > 0) {
      // Filtruj tylko przychody (kwoty dodatnie)
      const incomeTransactions = transactions
        .filter(t => t.amount > 0)
        .sort((a, b) => new Date(b.date) - new Date(a.date)); // Od najnowszych
      
      setAvailableTransactions(incomeTransactions);
    }
  }, [transactions]);
  
  // Po zmianie celu, ustaw domyślny opis
  useEffect(() => {
    if (goal) {
      setTransactionDescription(`Oszczędności na cel: ${goal.name}`);
    }
  }, [goal]);
  
  // Maksymalna kwota dostępna do alokacji
  const getMaxAllocation = () => {
    // Jeśli wybrano istniejącą transakcję
    if (source === 'existingTransaction' && selectedTransaction) {
      const transaction = availableTransactions.find(t => t.id === selectedTransaction);
      return transaction ? transaction.amount : 0;
    }
    
    // Jeśli wybrano nową transakcję, brak ograniczeń
    return Number.MAX_SAFE_INTEGER;
  };
  
  // Pozostała kwota do osiągnięcia celu
  const getRemainingGoalAmount = () => {
    if (!goal) return 0;
    return Math.max(0, goal.targetAmount - goal.currentAmount);
  };
  
  // Walidacja formularza
  const validateForm = () => {
    setError('');
    
    if (!amount || parseFloat(amount) <= 0) {
      setError('Kwota musi być większa od zera');
      return false;
    }
    
    const amountValue = parseFloat(amount);
    const maxAllocation = getMaxAllocation();
    
    if (source === 'existingTransaction' && amountValue > maxAllocation) {
      setError(`Kwota nie może przekraczać ${formatAmount(maxAllocation)}`);
      return false;
    }
    
    return true;
  };
  
  // Obsługa alokacji oszczędności
  const handleAllocate = () => {
    if (!validateForm()) return;
    
    const amountValue = parseFloat(amount);
    
    // Przygotuj dane alokacji
    const allocationData = {
      amount: amountValue,
      description: transactionDescription,
      date: new Date().toISOString().split('T')[0],
      source,
      transactionId: source === 'existingTransaction' ? selectedTransaction : null
    };
    
    // Wywołaj funkcję przekazaną przez props
    onAllocate(allocationData);
    
    // Resetuj formularz
    setAmount('');
    if (source === 'existingTransaction') {
      setSelectedTransaction('');
    }
  };
  
  // Wybierz maksymalną dostępną kwotę
  const handleSelectMax = () => {
    const maxAmount = Math.min(getMaxAllocation(), getRemainingGoalAmount());
    setAmount(maxAmount.toString());
  };

  return (
    <Paper elevation={3} sx={{ p: 3 }}>
      <Typography variant="h6" gutterBottom>
        Dodaj oszczędności do celu: {goal?.name}
      </Typography>
      
      {/* Informacje o celu */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="body2">
          Cel: {formatAmount(goal?.targetAmount || 0)}
        </Typography>
        <Typography variant="body2">
          Aktualne oszczędności: {formatAmount(goal?.currentAmount || 0)}
        </Typography>
        <Typography variant="body2">
          Pozostało do osiągnięcia: {formatAmount(getRemainingGoalAmount())}
        </Typography>
      </Box>
      
      <Divider sx={{ mb: 3 }} />
      
      {/* Wybór źródła oszczędności */}
      <FormControl fullWidth sx={{ mb: 3 }}>
        <InputLabel id="source-label">Źródło oszczędności</InputLabel>
        <Select
          labelId="source-label"
          id="source"
          value={source}
          label="Źródło oszczędności"
          onChange={(e) => {
            setSource(e.target.value);
            setAmount('');
            setSelectedTransaction('');
          }}
        >
          <MenuItem value="newTransaction">Nowa wpłata</MenuItem>
          <MenuItem value="existingTransaction">Z istniejącej transakcji</MenuItem>
        </Select>
      </FormControl>
      
      {/* Wybór istniejącej transakcji (jeśli wybrano to źródło) */}
      {source === 'existingTransaction' && (
        <FormControl fullWidth sx={{ mb: 3 }}>
          <InputLabel id="transaction-label">Wybierz transakcję</InputLabel>
          <Select
            labelId="transaction-label"
            id="transaction"
            value={selectedTransaction}
            label="Wybierz transakcję"
            onChange={(e) => {
              setSelectedTransaction(e.target.value);
              setAmount(''); // Resetuj kwotę przy zmianie transakcji
            }}
            disabled={availableTransactions.length === 0}
          >
            {availableTransactions.length > 0 ? (
              availableTransactions.map(transaction => (
                <MenuItem key={transaction.id} value={transaction.id}>
                  {transaction.title} - {formatAmount(transaction.amount)} ({new Date(transaction.date).toLocaleDateString('pl-PL')})
                </MenuItem>
              ))
            ) : (
              <MenuItem value="" disabled>Brak dostępnych transakcji</MenuItem>
            )}
          </Select>
          {source === 'existingTransaction' && availableTransactions.length === 0 && (
            <Alert severity="info" sx={{ mt: 1 }}>
              Brak dostępnych transakcji przychodowych
            </Alert>
          )}
        </FormControl>
      )}
      
      {/* Kwota oszczędności */}
      <Grid container spacing={2} alignItems="center" sx={{ mb: 3 }}>
        <Grid item xs>
          <TextField
            required
            fullWidth
            id="amount"
            label="Kwota oszczędności"
            type="number"
            InputProps={{ 
              inputProps: { min: 0, step: 0.01 },
              endAdornment: <InputAdornment position="end">PLN</InputAdornment>
            }}
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            error={!!error}
            helperText={error}
            disabled={source === 'existingTransaction' && !selectedTransaction}
          />
        </Grid>
        <Grid item>
          <Button 
            variant="outlined" 
            onClick={handleSelectMax}
            disabled={
              source === 'existingTransaction' && !selectedTransaction ||
              getMaxAllocation() === 0 ||
              getRemainingGoalAmount() === 0
            }
          >
            Max
          </Button>
        </Grid>
      </Grid>
      
      {/* Opis transakcji */}
      <TextField
        fullWidth
        id="description"
        label="Opis"
        value={transactionDescription}
        onChange={(e) => setTransactionDescription(e.target.value)}
        sx={{ mb: 3 }}
      />
      
      {/* Informacje pomocnicze */}
      {source === 'existingTransaction' && selectedTransaction && (
        <Alert severity="info" sx={{ mb: 3 }}>
          Wskazana kwota zostanie przypisana do celu oszczędnościowego z wybranej transakcji.
        </Alert>
      )}
      
      {/* Przycisk zatwierdzenia */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Button 
          variant="contained" 
          onClick={handleAllocate}
          disabled={loading || (source === 'existingTransaction' && !selectedTransaction)}
        >
          {loading ? <CircularProgress size={24} /> : 'Dodaj oszczędności'}
        </Button>
      </Box>
    </Paper>
  );
}

export default SavingsAllocation;